// src/hooks/firestore/operations.ts
import { useState, useCallback } from 'react';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  Timestamp,
  DocumentData,
  writeBatch
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAppSelector } from '@/store/hooks';
import { selectIsAuthenticated } from '@/store/slices/authSlice';
import type { RootState } from '@/store';

// Simplified permission check hook using Redux state
export const useCheckPermission = () => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const permissions = useAppSelector((state: RootState) => state.auth.permissions);

  const hasSpecificPermission = useCallback((permission: string): boolean => {
      if (!isAuthenticated) return false;
      // Admin always has permission
      if (permissions.includes('admin')) return true;
      // Editor has user and editor permissions
      if (permission === 'user' && permissions.includes('editor')) return true;
      // Direct check
      return permissions.includes(permission);
  }, [isAuthenticated, permissions]);

  const checkUserPermission = useCallback(() => {
    // Just check if user is authenticated
    if (!isAuthenticated) {
      throw new Error('User not authenticated');
    }
    // No need to return true explicitly, throwing error is the check
  }, [isAuthenticated]);

  return { hasPermission: hasSpecificPermission, checkUserPermission };
};

// --- CRUD Operation Hooks ---

// Generic hook for adding documents
export const useAddDocument = <T extends DocumentData>(collectionPath: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { checkUserPermission } = useCheckPermission(); // Use simplified check

  const addDocument = useCallback(async (data: Omit<T, 'id'>): Promise<string | null> => {
    setLoading(true);
    setError(null);
    try {
      checkUserPermission(); // Check if user is logged in
      
      // Add createdAt timestamp automatically
      const dataWithTimestamp = {
        ...data,
        createdAt: Timestamp.now(),
      };
      
      const docRef = await addDoc(collection(db, collectionPath), dataWithTimestamp);
      setLoading(false);
      return docRef.id;
    } catch (err) {
      console.error("Error adding document:", err);
      setError(err instanceof Error ? err : new Error('Failed to add document'));
      setLoading(false);
      return null;
    }
  }, [collectionPath, checkUserPermission]);

  return { addDocument, loading, error };
};

// Generic hook for updating documents
export const useUpdateDocument = <T extends DocumentData>(collectionPath: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { hasPermission } = useCheckPermission(); // Use Redux-based check

  const updateDocument = useCallback(async (id: string, data: Partial<T>): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      // Require 'editor' permission to update most documents
      // Adjust permission check if needed for specific collections
      if (!hasPermission('editor')) {
          throw new Error('Permission denied to update document.');
      }
      
      const docRef = doc(db, collectionPath, id);
      // Add updatedAt timestamp automatically
      const dataWithTimestamp = {
        ...data,
        updatedAt: Timestamp.now(),
      };
      await updateDoc(docRef, dataWithTimestamp);
      setLoading(false);
      return true;
    } catch (err) {
      console.error("Error updating document:", err);
      setError(err instanceof Error ? err : new Error('Failed to update document'));
      setLoading(false);
      return false;
    }
  }, [collectionPath, hasPermission]);

  return { updateDocument, loading, error };
};

// Generic hook for deleting documents
export const useDeleteDocument = (collectionPath: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { hasPermission } = useCheckPermission(); // Use Redux-based check

  const deleteDocument = useCallback(async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
       // Require 'editor' permission to delete most documents
      if (!hasPermission('editor')) {
          throw new Error('Permission denied to delete document.');
      }
      
      const docRef = doc(db, collectionPath, id);
      await deleteDoc(docRef);
      setLoading(false);
      return true;
    } catch (err) {
      console.error("Error deleting document:", err);
      setError(err instanceof Error ? err : new Error('Failed to delete document'));
      setLoading(false);
      return false;
    }
  }, [collectionPath, hasPermission]);

  return { deleteDocument, loading, error };
};

// Hook for performing batch writes
export const useBatchWrite = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const { hasPermission } = useCheckPermission(); // Use Redux-based check

    const executeBatch = useCallback(async (operations: 
        Array<{ 
            type: 'create' | 'update' | 'delete'; 
            path: string; // e.g., "locations" or "locations/docId"
            id?: string; // Required for update/delete
            data?: DocumentData; // Required for create/update
        }>
    ): Promise<boolean> => {
        setLoading(true);
        setError(null);

        try {
            // Basic permission check - require editor for any batch operation
            // More granular checks per operation could be added if needed
             if (!hasPermission('editor')) {
                throw new Error('Permission denied for batch operation.');
            }

            const batch = writeBatch(db);

            operations.forEach(op => {
                let docRef;
                switch (op.type) {
                    case 'create':
                        // For create, path is collection, let Firestore generate ID
                        docRef = doc(collection(db, op.path)); 
                        batch.set(docRef, { ...op.data, createdAt: Timestamp.now() });
                        break;
                    case 'update':
                        if (!op.id) throw new Error('Document ID required for update operation in batch.');
                        docRef = doc(db, op.path, op.id);
                        batch.update(docRef, { ...op.data, updatedAt: Timestamp.now() });
                        break;
                    case 'delete':
                         if (!op.id) throw new Error('Document ID required for delete operation in batch.');
                        docRef = doc(db, op.path, op.id);
                        batch.delete(docRef);
                        break;
                    default:
                        console.warn('Unsupported batch operation type:', op.type);
                }
            });

            await batch.commit();
            setLoading(false);
            return true;
        } catch (err) {
            console.error("Error executing batch write:", err);
            setError(err instanceof Error ? err : new Error('Batch operation failed'));
            setLoading(false);
            return false;
        }
    }, [hasPermission]);

    return { executeBatch, loading, error };
}; 